import api from "../api/api";

const MAX_MEMBER_WORKGROUP_PAGES = 500;
let memberWorkgroupCache = {
    token: "",
    list: null,
    loadingPromise: null,
};

function normalizeSearch(search) {
    return (search || "").trim().toLowerCase();
}

function matchesSearch(item, normalizedSearch) {
    if (!normalizedSearch) return true;
    return (item?.name || "").toLowerCase().includes(normalizedSearch);
}

function uniqueById(items) {
    const seen = new Set();
    const uniqueItems = [];

    (items || []).forEach((item) => {
        if (!item || seen.has(item.id)) return;
        seen.add(item.id);
        uniqueItems.push(item);
    });

    return uniqueItems;
}

function mapMemberWorkgroupItem(item) {
    return {
        id: Number(item?.id ?? 0),
        name: (item?.name || "").trim() || "Unnamed Member Workgroup",
        _pickerType: "memberWorkgroup",
        ownerOrganizationId: item?.ownerOrganizationId,
    };
}

async function loadAllMemberWorkgroups(parishSoftToken) {
    if (
        memberWorkgroupCache.list &&
        memberWorkgroupCache.token === parishSoftToken
    ) {
        return {
            success: true,
            list: memberWorkgroupCache.list,
            totalCount: memberWorkgroupCache.list.length,
        };
    }

    if (
        memberWorkgroupCache.loadingPromise &&
        memberWorkgroupCache.token === parishSoftToken
    ) {
        return memberWorkgroupCache.loadingPromise;
    }

    memberWorkgroupCache.token = parishSoftToken;
    memberWorkgroupCache.loadingPromise = (async () => {
        const allItems = [];
        let reportedTotalCount = null;

        for (let currentPage = 1; currentPage <= MAX_MEMBER_WORKGROUP_PAGES; currentPage++) {
            const result = await api.parishSoft.getMemberWorkgroupLookup(
                parishSoftToken,
                currentPage,
            );

            if (!result?.success) {
                return {
                    success: false,
                    list: [],
                    totalCount: 0,
                    errorMessage:
                        result?.errorMessage || "Failed to load member workgroups.",
                };
            }

            const mappedPage = Array.isArray(result?.list)
                ? result.list.map(mapMemberWorkgroupItem)
                : [];

            if (typeof result?.totalCount === "number") {
                reportedTotalCount = result.totalCount;
            }

            allItems.push(...mappedPage);

            if (mappedPage.length === 0) break;
            if (
                typeof reportedTotalCount === "number" &&
                allItems.length >= reportedTotalCount
            ) {
                break;
            }
        }

        const uniqueItems = uniqueById(allItems);
        memberWorkgroupCache.list = uniqueItems;

        return {
            success: true,
            list: uniqueItems,
            totalCount: uniqueItems.length,
        };
    })();

    const result = await memberWorkgroupCache.loadingPromise;
    memberWorkgroupCache.loadingPromise = null;

    if (!result?.success) {
        memberWorkgroupCache.list = null;
    }

    return result;
}

const memberWorkgroups = {
    load: async (search, page, pageSize, skipTotal) => {
        const parishSoftToken = localStorage.getItem("ParishSoftToken") || "";
        const result = await loadAllMemberWorkgroups(parishSoftToken);

        if (result?.success) {
            const normalizedSearch = normalizeSearch(search);
            const filteredList = (result.list || []).filter((item) =>
                matchesSearch(item, normalizedSearch),
            );
            const currentPage = Math.max(1, Number(page) || 1);
            const currentPageSize = Math.max(1, Number(pageSize) || 50);
            const startIndex = (currentPage - 1) * currentPageSize;
            const pagedList = filteredList.slice(
                startIndex,
                startIndex + currentPageSize,
            );

            return {
                success: true,
                list: pagedList,
                totalCount: filteredList.length,
            };
        }

        return {
            success: false,
            list: [],
            totalCount: 0,
            errorMessage:
                result?.errorMessage || "Failed to load member workgroups.",
        };
    },
};

export default memberWorkgroups;
